import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block, CheckCircleOutlined } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listWorkPoints, deactivateWorkPoint, reactivateWorkPoint, type WorkPointListItem } from '../../api/puncteLucru'
import { errorMessage } from '../../api/client'
import WorkPointFormDialog from './WorkPointFormDialog'

export default function WorkPointsPage() {
  const [formTarget, setFormTarget] = useState<WorkPointListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: WorkPointListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['administrare', 'puncte-lucru'], queryFn: listWorkPoints })

  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['administrare', 'puncte-lucru'] })

  const deactivateMutation = useMutation({ mutationFn: deactivateWorkPoint, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const reactivateMutation = useMutation({ mutationFn: reactivateWorkPoint, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<WorkPointListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 120, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'city', headerName: 'Localitate', width: 160, valueGetter: (v: string | null) => v ?? '' },
    {
      field: 'isDefault', headerName: 'Implicit', width: 100, sortable: false,
      renderCell: (p) => p.row.isDefault ? <Chip label="Implicit" size="small" color="primary" variant="outlined" /> : null,
    },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activ" size="small" color="success" variant="outlined" />
        : <Chip label="Inactiv" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Configurare</Typography>
        <Typography variant="inherit" color="text.primary">Puncte de lucru</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Puncte de lucru</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Punct de lucru nou</Button>
      </Box>

      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid
          rows={data ?? []} columns={columns} loading={isLoading} density="comfortable"
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50]}
        />
      </Box>

      <WorkPointFormDialog workPoint={formTarget} onClose={() => setFormTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          menuAnchor.row.isActive ? (
            <MenuItem key="deactivate" onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
              <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem key="reactivate" onClick={() => { closeMenu(); reactivateMutation.mutate(menuAnchor.row.id) }}>
              <ListItemIcon><CheckCircleOutlined fontSize="small" /></ListItemIcon><ListItemText>Reactivare</ListItemText>
            </MenuItem>
          ),
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
